import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import badIcon from "../../../../assets/connection_quality_bad.svg";
import mediumIcon from "../../../../assets/connection_quality_medium.svg";
import goodIcon from "../../../../assets/connection_quality_good.svg";
import { useConnectionMonitor } from "../../../MediaPlayback/hooks/useConnectionMonitor";
import { mapSocketFactory } from "../../../../utils/socketFactory";

const pickIconForQuality = (quality) => {
  switch (quality) {
    case "excellent":
      return goodIcon;
    case "good":
      return mediumIcon;
    case "poor":
    default:
      return badIcon;
  }
};

export default function ConnectionQualityIndicator({ className = "" }) {
  const pingMetrics = useConnectionMonitor();
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [isSocketConnected, setIsSocketConnected] = useState(() => {
    if (mapSocketFactory?.socket) {
      return mapSocketFactory.socket.connected;
    }
    return false;
  });
  const { connectionQuality } = useSelector(
    (state) => state.livekit
  );
  const { localAvatar } = useSelector((state) => state.auth);

  // Local state for connection metrics
  const [localConnectionMetrics, setLocalConnectionMetrics] = useState({
    quality: "unknown",
    latency: null,
    errorRate: 0,
    lastUpdated: null,
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!mapSocketFactory?.socket) {
      setIsSocketConnected(false);
      return;
    }

    const socket = mapSocketFactory.socket;
    const handleConnect = () => setIsSocketConnected(true);
    const handleDisconnect = () => setIsSocketConnected(false);

    setIsSocketConnected(socket.connected);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, []);

  const isStale =
    typeof pingMetrics.lastUpdated === "number"
      ? Date.now() - pingMetrics.lastUpdated > 15000
      : true;

  const derivedQuality = useMemo(() => {
    // Si offline, qualité poor
    if (!isOnline) {
      return "poor";
    }

    // Priorité aux pings HTTP : si les pings sont bons, on fait confiance aux pings
    // Le socket peut être en cours de connexion mais les pings mesurent la connexion réelle
    const hasGoodPings = pingMetrics.quality &&
      pingMetrics.quality !== "unknown" &&
      !isStale;

    if (hasGoodPings) {
      return pingMetrics.quality;
    }

    // Si pas de bons pings ET socket déconnecté → poor
    if (!isSocketConnected) {
      return "poor";
    }

    // Sinon, utiliser la qualité des pings (même si unknown)
    return pingMetrics.quality ?? "unknown";
  }, [
    isOnline,
    isSocketConnected,
    isStale,
    pingMetrics.quality,
  ]);

  // Assemble all connection stats into a single object
  const connectionStats = useMemo(() => ({
    quality: derivedQuality,
    latency: pingMetrics.latency,
    errorRate: pingMetrics.errorRate,
    lastUpdated: pingMetrics.lastUpdated,
  }), [
    derivedQuality,
    pingMetrics.latency,
    pingMetrics.errorRate,
    pingMetrics.lastUpdated,
  ]);

  // Update local connection metrics
  useEffect(() => {
    setLocalConnectionMetrics(connectionStats);
  }, [connectionStats]);

  // Emit connection quality to map socket immediately when stats change
  useEffect(() => {
    const isStale = !connectionStats.lastUpdated ||
      (Date.now() - connectionStats.lastUpdated > 15000);

    // Only emit if data is fresh, socket is connected, and we have a valid user
    if (!isStale && mapSocketFactory?.socket?.connected && localAvatar?.id) {
      mapSocketFactory.socket.emit("connectionQuality", {
        quality: connectionStats.quality,
        latency: connectionStats.latency,
        errorRate: connectionStats.errorRate,
        user_id: localAvatar?.id,
      });
    }
  }, [connectionStats, localAvatar?.id]);



  const effectiveQuality =
    derivedQuality && derivedQuality !== "unknown"
      ? derivedQuality
      : localConnectionMetrics.quality && localConnectionMetrics.quality !== "unknown"
        ? localConnectionMetrics.quality
        : connectionQuality || "unknown";

  return (
    <div className={`connection-quality-indicator ${className}`.trim()}>
      <img
        src={pickIconForQuality(effectiveQuality)}
        alt="Connection Quality Icon"
      />
    </div>
  );
}


